export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  tags: string[];
  helpful: number;
  notHelpful: number;
  lastUpdated: string;
}

export const faqs: FAQ[] = [
  {
    id: 'what-is-aquaarmour',
    question: 'What is Flexcrete?',
    answer: 'Flexcrete is a 2-component waterproof cementitious mortar. The liquid component is a blend of polymers while the dry component is a proprietary blend of cement and other admixtures. When mixed and applied, you achieve a waterproof membrane with incredible adhesion. Flexcrete exhibits concrete-like characteristics while being flexible enough to absorb structural movements and crack formation in substrates.',
    category: 'Product Information',
    tags: ['aquaarmour', 'product', 'cementitious', 'waterproof'],
    helpful: 125,
    notHelpful: 2,
    lastUpdated: '2024-01-20'
  },
  {
    id: 'aquaarmour-advantages',
    question: 'What are the main advantages of Flexcrete?',
    answer: 'Flexcrete offers maximum adhesion to all substrates, is designed for submerged applications, works on both positive and negative water pressure, is impervious to UV rays, resists all corrosive agents, is immune to freeze-thaw cycles, and protects against efflorescence. It\'s an all-around waterproofing solution for both interior and exterior applications.',
    category: 'Product Information',
    tags: ['advantages', 'benefits', 'features', 'aquaarmour'],
    helpful: 98,
    notHelpful: 1,
    lastUpdated: '2024-01-20'
  },
  {
    id: 'package-sizes',
    question: 'What sizes is Flexcrete available in?',
    answer: 'Flexcrete is available in three convenient sizes: ½ Gallon Jug (perfect for small repairs), 1 Gallon Jug (ideal for medium-sized projects), and 5 Gallon Pail (best for large applications). Choose the size based on your project scope.',
    category: 'Packaging',
    tags: ['packaging', 'sizes', 'purchasing'],
    helpful: 76,
    notHelpful: 0,
    lastUpdated: '2024-01-19'
  },
  {
    id: 'mixing-instructions',
    question: 'How do I properly mix Flexcrete?',
    answer: 'Add 1 measure of Flexcrete liquid to bucket first, then add 2 measures of Type M Mortar mix. Mix thoroughly for 2-3 minutes with a paddle mixer until homogenous with no clumps. Add remaining powder gradually until consistency of thick milkshake or pancake batter. Let false set for 90-120 seconds, then remix for 60-90 seconds. Apply immediately. NEVER add water to the mix.',
    category: 'Application',
    tags: ['mixing', 'preparation', 'instructions', 'how-to'],
    helpful: 142,
    notHelpful: 3,
    lastUpdated: '2024-01-19'
  },
  {
    id: 'pot-life',
    question: 'How long is the pot life of mixed Flexcrete?',
    answer: 'Once mixed, Flexcrete has a pot life of 20-25 minutes before the mix stiffens. You can extend pot life by mixing the material again or by adding a small amount (shot glass) of additional liquid and remixing. You can also mix remnants of a previous batch into a new batch.',
    category: 'Application',
    tags: ['pot-life', 'working-time', 'mixing'],
    helpful: 89,
    notHelpful: 2,
    lastUpdated: '2024-01-19'
  },
  {
    id: 'application-methods',
    question: 'What application methods can I use with Flexcrete?',
    answer: 'Flexcrete can be applied by paint roller (for large flat surfaces), paint brush (for detailed work), trowel (for thick coats), squeegee (for smooth finishes), or hopper gun (for spray applications). Choose the method based on your project needs and surface type. Contact us for guidance on hopper gun spray applications.',
    category: 'Application',
    tags: ['application', 'methods', 'tools', 'techniques'],
    helpful: 104,
    notHelpful: 1,
    lastUpdated: '2024-01-18'
  },
  {
    id: 'temperature-conditions',
    question: 'What temperature conditions are required for Flexcrete application?',
    answer: 'Only apply Flexcrete in ambient temperatures of 5°C - 35°C (40°F - 100°F). Do not apply in rain or under excessive temperatures. Check weather conditions - there should be no rain for the first 24 hours after application, and no frost for the first 24 hours. Consult us for guidance on borderline conditions.',
    category: 'Application Guidelines',
    tags: ['temperature', 'weather', 'conditions', 'application'],
    helpful: 95,
    notHelpful: 2,
    lastUpdated: '2024-01-18'
  },
  {
    id: 'substrate-preparation',
    question: 'How do I prepare the substrate for Flexcrete application?',
    answer: 'Remove all dust, loose materials, and grease stains. The substrate should be clean per ASTM guidelines / NF DTU section 21. If the substrate is porous, humidify to saturation before application. Wait 24 hours between each coat - you should not be able to mark the first coat with your nail before applying the second coat.',
    category: 'Application Guidelines',
    tags: ['substrate', 'preparation', 'surface-prep', 'cleaning'],
    helpful: 118,
    notHelpful: 2,
    lastUpdated: '2024-01-17'
  },
  {
    id: 'what-is-flashing',
    question: 'What is flashing and why is it important?',
    answer: 'Flashing is thin, impermeable material applied to joints, seams, and transitions on building exteriors to block water intrusion. It\'s your primary defense against water damage at critical areas like wall-to-floor joints, wall-to-deck transitions, roof level changes, and where different materials meet. Flexcrete is ideal for flashing applications, especially at wall-balcony and wall-deck cold joints.',
    category: 'Waterproofing Concepts',
    tags: ['flashing', 'waterproofing', 'joints', 'transitions'],
    helpful: 132,
    notHelpful: 1,
    lastUpdated: '2024-01-17'
  },
  {
    id: 'substrates-compatible',
    question: 'What substrates is Flexcrete compatible with?',
    answer: 'Flexcrete has exceptional adhesive properties and will adhere to almost all substrates including CMU blocks, poured concrete, gunite/shotcrete, polystyrene block, metal, ceramic or glass tile, and PVC. It works on all substrates commonly used in construction and leaves them ready to receive an additional layer on top.',
    category: 'Product Information',
    tags: ['substrates', 'compatibility', 'adhesion', 'materials'],
    helpful: 87,
    notHelpful: 0,
    lastUpdated: '2024-01-16'
  },
  {
    id: 'pool-spa-applications',
    question: 'Can Flexcrete be used for pools and spas?',
    answer: 'Yes! Flexcrete is designed for submerged applications including pools, spas, and fountains. It\'s highly recommended to waterproof behind the tile line to avoid efflorescence deposits. Flexcrete can be used to repair leaks around fittings, repair cracks, and can even serve as the final finish in a swimming pool or other water-holding vessel.',
    category: 'Applications',
    tags: ['pools', 'spas', 'submerged', 'fountains'],
    helpful: 156,
    notHelpful: 2,
    lastUpdated: '2024-01-16'
  },
  {
    id: 'basement-waterproofing',
    question: 'How does Flexcrete protect basements?',
    answer: 'Flexcrete protects basement interior walls from moisture intrusion and stains, prevents dampness, repairs cracks in basement walls, and seals window frames from water intrusion. It works on both positive and negative water pressure, making it ideal for below-grade applications where groundwater pressure is a concern.',
    category: 'Applications',
    tags: ['basement', 'below-grade', 'moisture', 'waterproofing'],
    helpful: 143,
    notHelpful: 1,
    lastUpdated: '2024-01-15'
  },
  {
    id: 'roof-applications',
    question: 'Can Flexcrete be used on roofs?',
    answer: 'Yes! Flexcrete is excellent for both flat and pitched roofs. It can repair rusted galvanized roof panels, seal chimney and utility vent flashing, repair cracked roof tiles, and provide flashing between different roof levels. Being UV resistant, Flexcrete is impervious to sun damage and protects against all weather conditions.',
    category: 'Applications',
    tags: ['roof', 'roofing', 'flashing', 'uv-resistant'],
    helpful: 129,
    notHelpful: 2,
    lastUpdated: '2024-01-15'
  },
  {
    id: 'balcony-deck-waterproofing',
    question: 'Why is flashing at wall-balcony joints so important?',
    answer: 'The wall-balcony cold joint is a critical waterproofing point. Water intrusion at this junction can cause moisture migration from below, loosening tile bonds and causing structural damage. Flexcrete flashing at this joint is highly recommended. Apply it to the entire balcony including the bottom side to prevent water migration and protect the structure.',
    category: 'Applications',
    tags: ['balcony', 'deck', 'flashing', 'cold-joint'],
    helpful: 167,
    notHelpful: 1,
    lastUpdated: '2024-01-14'
  },
  {
    id: 'efflorescence-prevention',
    question: 'How does Flexcrete prevent efflorescence?',
    answer: 'Flexcrete creates an impermeable barrier that prevents water and moisture from migrating through concrete and masonry. Since efflorescence is caused by water carrying salts to the surface, preventing moisture intrusion eliminates the problem. This is especially important for retaining walls, pool tile lines, planters, and water features.',
    category: 'Waterproofing Concepts',
    tags: ['efflorescence', 'prevention', 'moisture', 'salts'],
    helpful: 92,
    notHelpful: 0,
    lastUpdated: '2024-01-14'
  },
  {
    id: 'shower-bathroom-waterproofing',
    question: 'How do I waterproof a shower pan with Flexcrete?',
    answer: 'Clean and prepare the shower pan surface ensuring proper slope to drain. Apply Flexcrete to the entire shower pan floor and walls up to the splash zone, building up thickness at corners and wall-floor transitions. Wait 24 hours and apply a second coat. After curing, Flexcrete provides an excellent substrate for tile installation.',
    category: 'Applications',
    tags: ['shower', 'bathroom', 'tile-substrate', 'interior'],
    helpful: 138,
    notHelpful: 2,
    lastUpdated: '2024-01-13'
  },
  {
    id: 'freeze-thaw-resistance',
    question: 'Is Flexcrete immune to freeze-thaw cycles?',
    answer: 'Yes! Flexcrete is immune to freeze-thaw cycles, making it ideal for exterior applications in cold climates. The built-in elasticity allows the membrane to absorb movements in the structure without cracking, even when subjected to repeated freezing and thawing.',
    category: 'Product Information',
    tags: ['freeze-thaw', 'cold-climate', 'elasticity', 'durability'],
    helpful: 85,
    notHelpful: 1,
    lastUpdated: '2024-01-13'
  },
  {
    id: 'water-pressure-types',
    question: 'What is the difference between positive and negative water pressure?',
    answer: 'Positive water pressure is when water pushes against the waterproofing from the outside (like a pool or water tank). Negative water pressure is when water pushes from behind the waterproofing (like groundwater against a basement wall). Flexcrete works effectively against both types of water pressure.',
    category: 'Waterproofing Concepts',
    tags: ['water-pressure', 'positive', 'negative', 'technical'],
    helpful: 112,
    notHelpful: 3,
    lastUpdated: '2024-01-12'
  },
  {
    id: 'cistern-storage-tanks',
    question: 'Can Flexcrete be used for potable water storage?',
    answer: 'Yes! Flexcrete is suitable for potable water storage in cisterns and concrete holding tanks. It\'s especially recommended for RO (reverse osmosis) water storage as it resists the corrosive effects of RO water. The waterproof lining keeps tanks truly waterproof and prevents contamination from concrete leeching.',
    category: 'Applications',
    tags: ['cistern', 'storage-tank', 'potable-water', 'ro-water'],
    helpful: 94,
    notHelpful: 0,
    lastUpdated: '2024-01-12'
  },
  {
    id: 'coverage-calculation',
    question: 'How do I calculate how much Flexcrete I need?',
    answer: 'Calculate the total square footage of the area to be waterproofed. Consider that you\'ll need at least 2 coats for proper waterproofing. Add 10% extra for waste and overlap areas. Coverage will vary based on application method and substrate porosity. Contact us for specific coverage calculations for your project.',
    category: 'Planning',
    tags: ['coverage', 'calculation', 'quantity', 'planning'],
    helpful: 78,
    notHelpful: 2,
    lastUpdated: '2024-01-11'
  },
  {
    id: 'cure-time-between-coats',
    question: 'How long should I wait between coats?',
    answer: 'Wait 24 hours between each application of Flexcrete. The first coat should be fully cured - you should not be able to mark it with your nail before applying the second coat. Protect from rain and frost during the first 24 hours after each application.',
    category: 'Application Guidelines',
    tags: ['cure-time', 'drying', 'coating', 'timing'],
    helpful: 145,
    notHelpful: 1,
    lastUpdated: '2024-01-11'
  },
  {
    id: 'facade-stucco-applications',
    question: 'How does Flexcrete improve stucco applications?',
    answer: 'Flexcrete provides a waterproof substrate that prevents moisture intrusion and increases stucco adherence. It creates a better base for any cementitious façade coverings, prevents efflorescence, and protects the underlying structure from moisture damage. It can be sprayed or rolled onto façade surfaces.',
    category: 'Applications',
    tags: ['facade', 'stucco', 'exterior', 'substrate'],
    helpful: 71,
    notHelpful: 1,
    lastUpdated: '2024-01-10'
  },
  {
    id: 'retaining-wall-protection',
    question: 'Why waterproof retaining walls with Flexcrete?',
    answer: 'Waterproofing retaining walls prevents water intrusion from behind, eliminates unsightly efflorescence deposits, protects wall coverings, and prevents structural deterioration. Flexcrete creates a barrier against moisture while maintaining the structural integrity of the wall.',
    category: 'Applications',
    tags: ['retaining-wall', 'efflorescence', 'moisture-barrier'],
    helpful: 66,
    notHelpful: 0,
    lastUpdated: '2024-01-10'
  },
  {
    id: 'diy-vs-professional-aquaarmour',
    question: 'Can I apply Flexcrete myself or should I hire a professional?',
    answer: 'Many homeowners successfully apply Flexcrete themselves for simpler projects like basement walls, shower pans, and small repairs. However, complex applications like pools, large roof areas, or structural waterproofing may benefit from professional installation. Consider your experience, the project scope, and safety requirements. We provide detailed instructions for DIY applications.',
    category: 'General',
    tags: ['diy', 'professional', 'installation', 'guidance'],
    helpful: 102,
    notHelpful: 4,
    lastUpdated: '2024-01-09'
  }
];

export const faqCategories = [
  'All',
  'Product Information',
  'Application',
  'Application Guidelines',
  'Applications',
  'Waterproofing Concepts',
  'Planning',
  'General'
];
